import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, Navigate, useNavigate, useLocation, Outlet, NavLink, useParams } from "react-router-dom";
import { useCustomerAuth, formatApiErrorDetail } from "../../lib/useCustomerAuth";
import { useCart } from "../../lib/useCart";
import { useSettings } from "../../lib/useSettings";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Card } from "../../components/ui/card";
import {
  User, Lock, Mail, Package, MapPin, LogOut, Shield, Loader2,
  Plus, Pencil, Trash2, Home, CheckCircle2, ShoppingBag, AlertCircle, Heart, Sparkles,
  ChevronRight, ArrowLeft, Truck, Box, Check, XCircle, RotateCcw, Timer, CreditCard, Gift, Copy, Award, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

// --------- LOGIN PAGE ----------
export function CustomerLoginPage() {
  const { login, isAuthenticated } = useCustomerAuth();
  const { settings: s } = useSettings();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (isAuthenticated) return <Navigate to={loc.state?.from || "/account"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenue !");
      navigate(loc.state?.from || "/account");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || "Erreur de connexion");
    } finally { setLoading(false); }
  };

  return (
    <section className="min-h-[70vh] flex items-center justify-center py-14 px-4 bg-[hsl(var(--muted))]/10">
      <Card className="w-full max-w-md p-8" data-testid="customer-login-card">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[color:var(--brand-navy)]/10 mx-auto flex items-center justify-center mb-3">
            <User className="w-6 h-6 text-[color:var(--brand-navy)]" />
          </div>
          <h1 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">Mon compte</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Connectez-vous pour suivre vos commandes.</p>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="customer-login-form">
          <div>
            <Label>Email</Label>
            <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" data-testid="login-email" className="mt-1" />
          </div>
          <div>
            <Label>Mot de passe</Label>
            <Input type="password" required value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" data-testid="login-password" className="mt-1" />
          </div>
          {err && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{err}</span>
            </div>
          )}
          <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/90" data-testid="login-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
          </Button>
        </form>
        <div className="mt-5 text-center text-sm text-[hsl(var(--muted-foreground))]">
          Pas encore de compte ? <Link to="/account/register" className="text-[color:var(--brand-sky)] font-semibold hover:underline">Créer un compte</Link>
        </div>
      </Card>
    </section>
  );
}

// --------- REGISTER PAGE ----------
export function CustomerRegisterPage() {
  const { register, isAuthenticated } = useCustomerAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "", phone: "", accept_newsletter: false });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (isAuthenticated) return <Navigate to="/account" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await register(form);
      toast.success("Compte créé !");
      navigate("/account");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || "Erreur d'inscription");
    } finally { setLoading(false); }
  };

  return (
    <section className="min-h-[70vh] flex items-center justify-center py-14 px-4 bg-[hsl(var(--muted))]/10">
      <Card className="w-full max-w-md p-8" data-testid="customer-register-card">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[color:var(--brand-sky)]/10 mx-auto flex items-center justify-center mb-3">
            <Plus className="w-6 h-6 text-[color:var(--brand-sky)]" />
          </div>
          <h1 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">Créer un compte</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Suivez vos commandes en un coup d'œil.</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Prénom</Label><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="mt-1" data-testid="reg-firstname" /></div>
            <div><Label>Nom</Label><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="mt-1" data-testid="reg-lastname" /></div>
          </div>
          <div><Label>Email *</Label><Input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="reg-email" /></div>
          <div><Label>Téléphone</Label><Input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="reg-phone" /></div>
          <div>
            <Label>Mot de passe * <span className="text-[10px] text-[hsl(var(--muted-foreground))]">(8 caractères minimum)</span></Label>
            <Input type="password" required minLength={8} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="reg-password" />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Switch checked={form.accept_newsletter} onCheckedChange={v => setForm({ ...form, accept_newsletter: v })} />
            <Label className="text-xs cursor-pointer">Je souhaite recevoir les offres et nouveautés par email</Label>
          </div>
          {err && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{err}</span>
            </div>
          )}
          <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/90" data-testid="reg-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer mon compte"}
          </Button>
          <p className="text-[10px] text-center text-[hsl(var(--muted-foreground))]">
            En créant un compte, vous acceptez nos CGU & politique de confidentialité.
          </p>
        </form>
        <div className="mt-5 text-center text-sm text-[hsl(var(--muted-foreground))]">
          Déjà inscrit ? <Link to="/account/login" className="text-[color:var(--brand-sky)] font-semibold hover:underline">Se connecter</Link>
        </div>
      </Card>
    </section>
  );
}

// --------- ACCOUNT LAYOUT (protected) ----------
export function AccountLayout() {
  const { customer, loading, logout } = useCustomerAuth();
  const loc = useLocation();
  const navigate = useNavigate();

  if (loading) return <div className="py-20 text-center text-[hsl(var(--muted-foreground))]"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  if (!customer) return <Navigate to="/account/login" state={{ from: loc.pathname }} replace />;

  const tabs = [
    { to: "/account", label: "Tableau de bord", icon: User, end: true },
    { to: "/account/orders", label: "Mes commandes", icon: Package },
    { to: "/account/wallet", label: "Wallet & Parrainage", icon: Gift },
    { to: "/account/favorites", label: "Mes favoris", icon: Heart },
    { to: "/account/addresses", label: "Mes adresses", icon: MapPin },
    { to: "/account/profile", label: "Mon profil", icon: Shield },
  ];

  return (
    <section className="py-10 md:py-14 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold">Espace client</div>
          <h1 className="font-display text-3xl md:text-4xl font-black text-[color:var(--brand-navy)] mt-1">
            Bonjour{customer.first_name ? `, ${customer.first_name}` : ""} <span className="wave">👋</span>
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Gérez vos commandes, adresses et préférences.</p>
        </div>

        <div className="grid md:grid-cols-[240px_1fr] gap-6">
          <aside className="space-y-1" data-testid="account-sidebar">
            {tabs.map(t => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-colors ${isActive ? "bg-[color:var(--brand-navy)] text-white font-semibold" : "hover:bg-[hsl(var(--muted))]/50 text-[hsl(var(--muted-foreground))]"}`}
                data-testid={`account-tab-${t.to.split("/").pop() || "dashboard"}`}
              >
                <t.icon className="w-4 h-4" />
                <span className="text-sm">{t.label}</span>
              </NavLink>
            ))}
            <button
              type="button"
              onClick={() => { logout(); toast.success("Déconnecté"); navigate("/"); }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors text-sm mt-4"
              data-testid="account-logout"
            >
              <LogOut className="w-4 h-4" />Se déconnecter
            </button>
          </aside>
          <main><Outlet /></main>
        </div>
      </div>
    </section>
  );
}

// --------- DASHBOARD TAB ----------
export function AccountDashboard() {
  const { customer, listOrders } = useCustomerAuth();
  const [ordersCount, setOrdersCount] = useState(0);
  useEffect(() => { listOrders().then(o => setOrdersCount((o || []).length)).catch(() => {}); }, []); // eslint-disable-line

  return (
    <div className="grid md:grid-cols-3 gap-4">
      <Card className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Commandes</div>
            <div className="text-3xl font-display font-black text-[color:var(--brand-navy)] mt-1">{ordersCount}</div>
          </div>
          <Package className="w-8 h-8 text-[color:var(--brand-sky)]/50" />
        </div>
        <Link to="/account/orders" className="text-xs text-[color:var(--brand-sky)] hover:underline mt-3 inline-block">Voir mes commandes →</Link>
      </Card>
      <Card className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Adresses</div>
            <div className="text-3xl font-display font-black text-[color:var(--brand-navy)] mt-1">{(customer?.addresses || []).length}</div>
          </div>
          <MapPin className="w-8 h-8 text-[color:var(--brand-sky)]/50" />
        </div>
        <Link to="/account/addresses" className="text-xs text-[color:var(--brand-sky)] hover:underline mt-3 inline-block">Gérer →</Link>
      </Card>
      <Card className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Compte</div>
            <div className="text-base font-display font-bold text-[color:var(--brand-navy)] mt-1 truncate">{customer?.email}</div>
          </div>
          <User className="w-8 h-8 text-[color:var(--brand-sky)]/50" />
        </div>
        <Link to="/account/profile" className="text-xs text-[color:var(--brand-sky)] hover:underline mt-3 inline-block">Éditer →</Link>
      </Card>
      <Card className="p-5 md:col-span-3 bg-[color:var(--brand-navy)] text-white">
        <div className="flex items-center gap-3 mb-2">
          <ShoppingBag className="w-5 h-5 text-[color:var(--brand-sky)]" />
          <h3 className="font-display font-bold">Continuez vos achats</h3>
        </div>
        <p className="text-sm text-white/80 mb-3">Découvrez notre sélection de produits et bénéficiez de la livraison rapide ou du Click & Collect.</p>
        <Link to="/shop" className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-white text-[color:var(--brand-navy)] text-sm font-semibold hover:bg-white/90 transition">Parcourir la boutique</Link>
      </Card>
    </div>
  );
}

// --------- ORDERS TAB ----------
const STATUS_LABELS = {
  paid: { label: "Payée", cls: "bg-emerald-100 text-emerald-800" },
  preparing: { label: "En préparation", cls: "bg-amber-100 text-amber-800" },
  ready: { label: "Prête", cls: "bg-sky-100 text-sky-800" },
  shipped: { label: "Expédiée", cls: "bg-indigo-100 text-indigo-800" },
  collected: { label: "Retirée", cls: "bg-slate-200 text-slate-800" },
  completed: { label: "Terminée", cls: "bg-slate-200 text-slate-800" },
  refunded: { label: "Remboursée", cls: "bg-red-100 text-red-800" },
};
export function AccountOrders() {
  const { listOrders } = useCustomerAuth();
  const [orders, setOrders] = useState(null);
  useEffect(() => { listOrders().then(setOrders).catch(() => setOrders([])); }, []); // eslint-disable-line

  return (
    <div>
      <h2 className="font-display font-bold text-xl text-[color:var(--brand-navy)] mb-4">Historique des commandes</h2>
      {orders === null ? (
        <div className="text-center py-10 text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…</div>
      ) : orders.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <Package className="w-12 h-12 mx-auto text-[hsl(var(--muted-foreground))]/30 mb-3" />
          <div className="font-display font-bold text-[color:var(--brand-navy)]">Aucune commande pour le moment</div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Votre historique apparaîtra ici dès votre première commande.</p>
          <Link to="/shop" className="mt-4 inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-[color:var(--brand-navy)] text-white text-sm font-semibold hover:bg-[color:var(--brand-navy)]/90 transition">
            Découvrir la boutique
          </Link>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="orders-list">
          {orders.map(o => {
            const st = STATUS_LABELS[o.status] || { label: o.status || "En cours", cls: "bg-slate-100 text-slate-700" };
            const itemCount = (o.line_items || []).reduce((s, l) => s + (l.qty || 0), 0);
            return (
              <Link to={`/account/orders/${o.id}`} key={o.id} className="block group" data-testid={`order-card-${o.id}`}>
                <Card className="p-4 group-hover:border-[color:var(--brand-sky)]/60 group-hover:shadow-md transition">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-[hsl(var(--muted-foreground))]">#{o.id.slice(0, 8)}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">{itemCount} article{itemCount > 1 ? "s" : ""}</span>
                      </div>
                      {o.line_items?.length > 0 && (
                        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1.5 line-clamp-1">
                          {o.line_items.map(l => `${l.qty}× ${l.name}`).join(" · ")}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0 flex items-center gap-3">
                      <div>
                        <div className="font-bold text-[color:var(--brand-navy)]">{(o.total_cents / 100).toFixed(2)} {(o.currency || "eur").toUpperCase()}</div>
                        <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{new Date(o.created_at).toLocaleDateString("fr-FR")}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] group-hover:text-[color:var(--brand-sky)] transition" />
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}


// --------- ORDER DETAIL TAB ----------
const TIMELINE_ICONS = { paid: CreditCard, preparing: Box, ready: Check, shipped: Truck, collected: Check, completed: CheckCircle2 };

export function AccountOrderDetail() {
  const { oid } = useParams();
  const [data, setData] = useState(null);      // { order, timeline }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { settings: s } = useSettings();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/customer/orders/${oid}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("msc_customer_token") || ""}` },
    })
      .then(r => { if (alive) setData(r.data); })
      .catch(e => { if (alive) setError(e?.response?.data?.detail || "Commande introuvable"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [oid]);

  if (loading) {
    return <div className="text-center py-10 text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…</div>;
  }
  if (error) {
    return (
      <Card className="p-8 text-center border-dashed">
        <AlertCircle className="w-10 h-10 mx-auto text-[color:var(--brand-red)] mb-3" />
        <div className="font-bold text-[color:var(--brand-navy)]">{error}</div>
        <Link to="/account/orders" className="mt-4 inline-flex items-center gap-1 text-sm text-[color:var(--brand-sky)] font-semibold"><ArrowLeft className="w-3.5 h-3.5" /> Retour aux commandes</Link>
      </Card>
    );
  }

  const { order, timeline } = data;
  const ref = `#${(order.id || "").slice(0, 8).toUpperCase()}`;
  const currency = (order.currency || "eur").toUpperCase();
  const total = (order.total_cents / 100).toFixed(2);

  const cancelled = timeline.terminal && (timeline.status === "cancelled" || timeline.status === "refunded");

  return (
    <div className="space-y-5" data-testid="order-detail-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/account/orders" className="inline-flex items-center gap-1 text-sm text-[color:var(--brand-navy)] font-semibold hover:text-[color:var(--brand-sky)]" data-testid="back-to-orders">
          <ArrowLeft className="w-4 h-4" /> Mes commandes
        </Link>
        <div className="text-[11px] text-[hsl(var(--muted-foreground))] font-mono">{new Date(order.created_at).toLocaleString("fr-FR")}</div>
      </div>

      <Card className="p-6 border" data-testid="order-detail-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--brand-sky)] font-bold mb-1">Commande {ref}</div>
            <h2 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">{timeline.label}</h2>
            <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              {order.fulfillment_type === "pickup" ? "Retrait en point de collecte" : "Livraison à domicile"}
              {order.pickup_location_name && <> · <strong>{order.pickup_location_name}</strong></>}
              {order.pickup_slot_at && <> · {new Date(order.pickup_slot_at).toLocaleString("fr-FR")}</>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Total</div>
            <div className="font-display font-bold text-2xl text-[color:var(--brand-navy)]">{total} {currency}</div>
          </div>
        </div>
      </Card>

      {/* Timeline */}
      {!cancelled && timeline.steps.length > 0 && (
        <Card className="p-6 border" data-testid="order-timeline">
          <h3 className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--brand-navy)] font-bold mb-5 flex items-center gap-1.5">
            <Timer className="w-3.5 h-3.5" /> Suivi de la commande
          </h3>
          <ol className="relative">
            {timeline.steps.map((step, i) => {
              const Icon = TIMELINE_ICONS[step.key] || Package;
              const isLast = i === timeline.steps.length - 1;
              return (
                <li key={step.key} className="flex items-start gap-4 relative pb-6 last:pb-0" data-testid={`timeline-step-${step.key}`}>
                  {!isLast && (
                    <span
                      aria-hidden
                      className={`absolute left-[18px] top-10 bottom-0 w-0.5 ${step.done ? "bg-[color:var(--brand-navy)]" : "bg-[hsl(var(--border))]"}`}
                    />
                  )}
                  <div
                    className={`relative z-10 w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition ${
                      step.done
                        ? "bg-[color:var(--brand-navy)] text-white shadow-md"
                        : step.active
                        ? "bg-[color:var(--brand-sky)] text-white animate-pulse shadow-md"
                        : "bg-[hsl(var(--muted))]/60 text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))]"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 pt-1.5">
                    <div className={`font-semibold ${step.done ? "text-[color:var(--brand-navy)]" : "text-[hsl(var(--muted-foreground))]"}`}>{step.label}</div>
                    {step.at && step.done && (
                      <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">
                        {new Date(step.at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                        {" · "}
                        {new Date(step.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    )}
                    {step.active && !step.done && (
                      <div className="text-[11px] font-semibold text-[color:var(--brand-sky)] mt-0.5">En cours…</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </Card>
      )}

      {cancelled && (
        <Card className={`p-5 border ${timeline.status === "refunded" ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200"}`}>
          <div className="flex items-start gap-3">
            {timeline.status === "refunded" ? <RotateCcw className="w-5 h-5 text-amber-700 shrink-0" /> : <XCircle className="w-5 h-5 text-red-700 shrink-0" />}
            <div>
              <div className={`font-bold ${timeline.status === "refunded" ? "text-amber-900" : "text-red-900"}`}>{timeline.label}</div>
              <div className="text-sm text-[hsl(var(--foreground))]/70 mt-1">
                {timeline.status === "refunded"
                  ? "Les fonds seront crédités sous 3 à 10 jours ouvrés selon votre banque."
                  : "Pour toute question, contactez-nous."}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Line items */}
      <Card className="p-6 border" data-testid="order-line-items">
        <h3 className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--brand-navy)] font-bold mb-4">Articles</h3>
        <div className="divide-y divide-[hsl(var(--border))]">
          {(order.line_items || []).map((l, i) => (
            <div key={i} className="flex items-center gap-3 py-3" data-testid={`order-line-${i}`}>
              {l.image ? (
                <img src={l.image} alt="" className="w-14 h-14 rounded-lg object-cover border" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-[hsl(var(--muted))]/40 flex items-center justify-center"><Package className="w-5 h-5 text-[hsl(var(--muted-foreground))]/40" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[color:var(--brand-navy)] truncate">{l.name}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">Quantité : <strong>{l.qty}</strong></div>
              </div>
              <div className="text-right">
                <div className="font-bold text-[color:var(--brand-navy)]">{((l.price_cents * l.qty) / 100).toFixed(2)} {currency}</div>
                <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{(l.price_cents / 100).toFixed(2)} {currency} / unité</div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between items-center pt-4 mt-2 border-t">
          <div className="text-sm font-semibold text-[color:var(--brand-navy)]">Total TTC</div>
          <div className="font-display font-bold text-xl text-[color:var(--brand-navy)]">{total} {currency}</div>
        </div>
      </Card>

      {s?.email && (
        <div className="text-center text-xs text-[hsl(var(--muted-foreground))] flex items-center justify-center gap-4 flex-wrap">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/customer/orders/${order.id}/invoice/download`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-sky)] text-white font-bold text-xs transition"
            data-testid="download-invoice"
            onClick={async (e) => {
              e.preventDefault();
              const token = localStorage.getItem("msc_customer_token") || "";
              const r = await fetch(e.currentTarget.href, { headers: { Authorization: `Bearer ${token}` } });
              if (!r.ok) return;
              const blob = await r.blob();
              const url = URL.createObjectURL(blob);
              window.open(url, "_blank");
              setTimeout(() => URL.revokeObjectURL(url), 60000);
            }}
          >
            <CreditCard className="w-3.5 h-3.5" /> Télécharger la facture
          </a>
          <span>Une question ? <a href={`mailto:${s.email}?subject=Commande%20${ref}`} className="text-[color:var(--brand-sky)] underline font-semibold">{s.email}</a></span>
        </div>
      )}
    </div>
  );
}


// --------- ADDRESSES TAB ----------
export function AccountFavorites() {
  const [items, setItems] = useState(null);
  const { settings: s } = useSettings();
  const load = () => {
    import("../../lib/useWishlist"); // ensure chunk
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wishlist`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("msc_customer_token") || ""}` },
    }).then(r => setItems(r.data.items || [])).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, []);

  const removeFav = async (pid) => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/wishlist/toggle`, { product_id: pid }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("msc_customer_token") || ""}` },
      });
      setItems(arr => (arr || []).filter(x => x.id !== pid));
      toast.success("Retiré");
    } catch { toast.error("Erreur"); }
  };

  const threshold = s?.wishlist_discount_after_days ?? 7;
  const percent = s?.wishlist_discount_percent ?? 10;

  return (
    <div>
      <h2 className="font-display font-bold text-xl text-[color:var(--brand-navy)] mb-4">Mes favoris</h2>
      {items === null ? (
        <div className="text-center py-10 text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…</div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center border-dashed" data-testid="favorites-empty">
          <Heart className="w-12 h-12 mx-auto text-[hsl(var(--muted-foreground))]/30 mb-3" />
          <div className="font-display font-bold text-[color:var(--brand-navy)]">Aucun favori pour le moment</div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Cliquez sur le cœur ❤️ d'un produit pour le sauvegarder ici.</p>
          <Link to="/shop" className="mt-4 inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-[color:var(--brand-navy)] text-white text-sm font-semibold hover:bg-[color:var(--brand-navy)]/90 transition">
            Découvrir la boutique
          </Link>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="favorites-list">
          {items.map(p => {
            const days = p.favorited_at ? Math.floor((Date.now() - new Date(p.favorited_at).getTime()) / 86400000) : 0;
            const eligible = s?.wishlist_enabled && days >= threshold && percent > 0;
            return (
              <Card key={p.id} className="p-3 flex gap-3" data-testid={`fav-item-${p.id.slice(0, 8)}`}>
                <Link to={`/shop/${p.slug}`} className="shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-[hsl(var(--muted))]/30">
                  {p.featured_image ? (
                    <img src={p.featured_image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : <Package className="w-8 h-8 text-[hsl(var(--muted-foreground))]/30 m-auto mt-7" />}
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/shop/${p.slug}`} className="font-display font-bold text-sm text-[color:var(--brand-navy)] line-clamp-1 hover:text-[color:var(--brand-sky)]">{p.name}</Link>
                  <div className="text-[10px] text-[hsl(var(--muted-foreground))]">Ajouté il y a {days} jour{days > 1 ? "s" : ""}</div>
                  <div className="font-bold text-[color:var(--brand-navy)] mt-1">{((p.price_cents || 0) / 100).toFixed(2)} €</div>
                  {eligible && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-[color:var(--brand-red)] bg-[color:var(--brand-red)]/10 px-2 py-0.5 rounded-full">
                      <Sparkles className="w-2.5 h-2.5" />-{percent}% disponible
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeFav(p.id)}
                    className="mt-2 text-[11px] text-red-600 hover:underline inline-flex items-center gap-1"
                    data-testid={`fav-remove-${p.id.slice(0, 8)}`}
                  >
                    <Trash2 className="w-3 h-3" />Retirer
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --------- ADDRESSES TAB ----------
export function AccountAddresses() {
  const { listAddresses, createAddress, updateAddress, deleteAddress } = useCustomerAuth();
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = async () => setItems(await listAddresses());
  useEffect(() => { load(); }, []); // eslint-disable-line

  const emptyAddr = () => ({ label: "Domicile", first_name: "", last_name: "", company: "", street: "", street2: "", postal_code: "", city: "", country: "France", phone: "", is_default_shipping: false, is_default_billing: false });

  const save = async () => {
    setSaving(true);
    try {
      if (edit.id) await updateAddress(edit.id, edit); else await createAddress(edit);
      toast.success("Adresse enregistrée");
      setEdit(null); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette adresse ?")) return;
    await deleteAddress(id); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-xl text-[color:var(--brand-navy)]">Mes adresses</h2>
        <Button onClick={() => setEdit(emptyAddr())} className="bg-[color:var(--brand-red)]" data-testid="new-address-btn">
          <Plus className="w-4 h-4 mr-1" />Nouvelle adresse
        </Button>
      </div>
      {items.length === 0 ? (
        <Card className="p-8 text-center border-dashed">
          <MapPin className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))]/30 mb-2" />
          <div className="text-sm text-[hsl(var(--muted-foreground))]">Aucune adresse enregistrée. Ajoutez-en une pour simplifier vos commandes.</div>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map(a => (
            <Card key={a.id} className="p-4 border" data-testid={`address-card-${a.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[color:var(--brand-sky)] font-bold">{a.label}</div>
                  <div className="font-display font-bold text-[color:var(--brand-navy)] mt-1">{a.first_name} {a.last_name}</div>
                  {a.company && <div className="text-sm text-[hsl(var(--muted-foreground))]">{a.company}</div>}
                  <div className="text-sm mt-2">{a.street}{a.street2 ? `, ${a.street2}` : ""}</div>
                  <div className="text-sm">{a.postal_code} {a.city}</div>
                  <div className="text-sm text-[hsl(var(--muted-foreground))]">{a.country}{a.phone ? ` · ${a.phone}` : ""}</div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {a.is_default_shipping && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5" />Livraison</span>}
                    {a.is_default_billing && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[color:var(--brand-sky)]/15 text-[color:var(--brand-sky)]">Facturation</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setEdit(a)}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => del(a.id)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      {edit && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !saving && setEdit(null)}>
          <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center px-5 py-4 border-b">
              <h3 className="font-display font-bold text-lg">{edit.id ? "Modifier l'adresse" : "Nouvelle adresse"}</h3>
              <button onClick={() => setEdit(null)}>✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div><Label>Libellé</Label><Input value={edit.label} onChange={e => setEdit({ ...edit, label: e.target.value })} placeholder="Domicile, Bureau…" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prénom</Label><Input value={edit.first_name} onChange={e => setEdit({ ...edit, first_name: e.target.value })} /></div>
                <div><Label>Nom</Label><Input value={edit.last_name} onChange={e => setEdit({ ...edit, last_name: e.target.value })} /></div>
              </div>
              <div><Label>Société (opt.)</Label><Input value={edit.company} onChange={e => setEdit({ ...edit, company: e.target.value })} /></div>
              <div><Label>Adresse *</Label><Input value={edit.street} onChange={e => setEdit({ ...edit, street: e.target.value })} required /></div>
              <div><Label>Complément</Label><Input value={edit.street2} onChange={e => setEdit({ ...edit, street2: e.target.value })} /></div>
              <div className="grid grid-cols-[120px_1fr] gap-3">
                <div><Label>CP *</Label><Input value={edit.postal_code} onChange={e => setEdit({ ...edit, postal_code: e.target.value })} required /></div>
                <div><Label>Ville *</Label><Input value={edit.city} onChange={e => setEdit({ ...edit, city: e.target.value })} required /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Pays</Label><Input value={edit.country} onChange={e => setEdit({ ...edit, country: e.target.value })} /></div>
                <div><Label>Téléphone</Label><Input value={edit.phone} onChange={e => setEdit({ ...edit, phone: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <div className="flex items-center gap-2"><Switch checked={edit.is_default_shipping} onCheckedChange={v => setEdit({ ...edit, is_default_shipping: v })} /><Label>Livraison par défaut</Label></div>
                <div className="flex items-center gap-2"><Switch checked={edit.is_default_billing} onCheckedChange={v => setEdit({ ...edit, is_default_billing: v })} /><Label>Facturation par défaut</Label></div>
              </div>
            </div>
            <div className="px-5 py-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEdit(null)}>Annuler</Button>
              <Button onClick={save} disabled={saving || !edit.street || !edit.city || !edit.postal_code} className="bg-[color:var(--brand-navy)]" data-testid="save-address-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --------- PROFILE TAB ----------
export function AccountProfile() {
  const { customer, updateProfile, changePassword } = useCustomerAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", accept_newsletter: false });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    if (customer) setForm({
      first_name: customer.first_name || "", last_name: customer.last_name || "",
      phone: customer.phone || "", accept_newsletter: !!customer.accept_newsletter,
    });
  }, [customer]);

  const saveProfile = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await updateProfile(form); toast.success("Profil mis à jour"); }
    catch (e2) { toast.error(formatApiErrorDetail(e2?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const savePw = async (e) => {
    e.preventDefault(); setPwSaving(true);
    try { await changePassword(pw.current_password, pw.new_password); toast.success("Mot de passe mis à jour"); setPw({ current_password: "", new_password: "" }); }
    catch (e2) { toast.error(formatApiErrorDetail(e2?.response?.data?.detail)); }
    finally { setPwSaving(false); }
  };

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <h2 className="font-display font-bold text-lg text-[color:var(--brand-navy)] mb-4 flex items-center gap-2"><User className="w-4 h-4" />Informations personnelles</h2>
        <form onSubmit={saveProfile} className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Prénom</Label><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="mt-1" /></div>
            <div><Label>Nom</Label><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="mt-1" /></div>
          </div>
          <div><Label className="flex items-center gap-1.5"><Mail className="w-3 h-3" />Email</Label><Input value={customer?.email || ""} disabled className="mt-1 bg-[hsl(var(--muted))]/30" /></div>
          <div><Label>Téléphone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
          <div className="flex items-center gap-2 pt-2"><Switch checked={form.accept_newsletter} onCheckedChange={v => setForm({ ...form, accept_newsletter: v })} /><Label className="text-sm cursor-pointer">Recevoir la newsletter</Label></div>
          <Button type="submit" disabled={saving} className="bg-[color:var(--brand-navy)]" data-testid="save-profile-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </form>
      </Card>
      <Card className="p-5">
        <h2 className="font-display font-bold text-lg text-[color:var(--brand-navy)] mb-4 flex items-center gap-2"><Lock className="w-4 h-4" />Changer mon mot de passe</h2>
        <form onSubmit={savePw} className="space-y-3">
          <div><Label>Mot de passe actuel</Label><Input type="password" required value={pw.current_password} onChange={e => setPw({ ...pw, current_password: e.target.value })} className="mt-1" /></div>
          <div><Label>Nouveau mot de passe (8 car. min)</Label><Input type="password" required minLength={8} value={pw.new_password} onChange={e => setPw({ ...pw, new_password: e.target.value })} className="mt-1" /></div>
          <Button type="submit" disabled={pwSaving || !pw.current_password || !pw.new_password} className="bg-[color:var(--brand-navy)]">
            {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Mettre à jour"}
          </Button>
        </form>
      </Card>
    </div>
  );
}


// --------- ORDER CONFIRMATION (post-Stripe redirect) ----------
export function OrderConfirmationPage() {
  const { settings: s } = useSettings();
  const [state, setState] = useState({ status: "polling", payment_status: null, order_id: null, amount_cents: 0, currency: "eur", attempts: 0 });
  const [err, setErr] = useState("");
  const sessionId = new URLSearchParams(window.location.search).get("session_id") || "";
  const { clear: clearCart } = useCart();

  useEffect(() => {
    if (!sessionId) { setErr("Session de paiement manquante dans l'URL."); return; }
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    let cancelled = false;
    const MAX_ATTEMPTS = 10;
    let attempt = 0;

    const poll = async () => {
      if (cancelled) return;
      attempt += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${encodeURIComponent(sessionId)}`);
        if (cancelled) return;
        setState({ ...data, attempts: attempt });
        if (data.status === "complete" && data.payment_status === "paid") {
          try { clearCart(); } catch (_) { /* cart already cleared */ }
          return;
        }
        if (data.status === "expired") return;
        if (attempt >= MAX_ATTEMPTS) {
          setErr("Le paiement prend plus de temps que prévu. Vous pouvez consulter vos commandes dans votre espace client.");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        if (attempt >= MAX_ATTEMPTS) setErr("Impossible de vérifier le paiement. Contactez-nous si le montant a été débité.");
        else setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line

  const paid = state.status === "complete" && state.payment_status === "paid";
  const failed = !!err || state.status === "expired";

  return (
    <section className="min-h-[70vh] flex items-center justify-center py-14 px-4 bg-[hsl(var(--muted))]/10">
      <Card className="w-full max-w-xl p-8 text-center" data-testid="order-confirmation-card">
        {paid ? (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">Paiement confirmé 🎉</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Merci pour votre commande auprès de <b>{s?.company_name || "notre boutique"}</b>.</p>
            <div className="mt-6 rounded-xl bg-[hsl(var(--muted))]/30 p-4 text-left space-y-2" data-testid="order-summary">
              {state.order_id && <div className="flex justify-between text-sm"><span className="text-[hsl(var(--muted-foreground))]">N° commande</span><span className="font-mono font-semibold">#{state.order_id.slice(0, 8)}</span></div>}
              <div className="flex justify-between text-sm"><span className="text-[hsl(var(--muted-foreground))]">Montant</span><span className="font-bold text-[color:var(--brand-navy)]">{(state.amount_cents / 100).toFixed(2)} {(state.currency || "eur").toUpperCase()}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[hsl(var(--muted-foreground))]">Statut</span><span className="font-semibold text-emerald-700">Payée</span></div>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/account/orders" className="h-11 px-6 rounded-full bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/90 text-white font-semibold text-sm inline-flex items-center justify-center transition" data-testid="go-to-orders-btn">Voir mes commandes</Link>
              <Link to="/shop" className="h-11 px-6 rounded-full border border-[color:var(--brand-navy)] text-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/5 font-semibold text-sm inline-flex items-center justify-center transition">Retour à la boutique</Link>
            </div>
          </>
        ) : failed ? (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 mx-auto flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">Paiement non confirmé</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{err || "Le paiement a expiré ou a été annulé."}</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/shop" className="h-11 px-6 rounded-full bg-[color:var(--brand-navy)] text-white font-semibold text-sm inline-flex items-center justify-center">Retour à la boutique</Link>
              <Link to="/account/orders" className="h-11 px-6 rounded-full border border-[color:var(--brand-navy)] text-[color:var(--brand-navy)] font-semibold text-sm inline-flex items-center justify-center">Mes commandes</Link>
            </div>
          </>
        ) : (
          <>
            <div className="w-16 h-16 rounded-full bg-[color:var(--brand-sky)]/10 mx-auto flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-[color:var(--brand-sky)] animate-spin" />
            </div>
            <h1 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">Vérification du paiement…</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Tentative {state.attempts}/10 — ne fermez pas cette page.</p>
          </>
        )}
      </Card>
    </section>
  );
}


// --------- WALLET & REFERRAL TAB (Phase 15) ----------
export function AccountWallet() {
  const { token } = useCustomerAuth();
  const { settings } = useSettings();
  const [state, setState] = useState({ loading: true, balance_cents: 0, referral_code: "", transactions: [] });
  const [copied, setCopied] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const currency = settings?.currency || "EUR";
  const locale = settings?.locale || "fr-FR";

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/customer/wallet`, { headers: { Authorization: `Bearer ${token}` } });
      setState({ loading: false, balance_cents: data.balance_cents || 0, referral_code: data.referral_code || "", transactions: data.transactions || [] });
    } catch (e) {
      setState(s => ({ ...s, loading: false }));
      toast.error("Impossible de charger votre wallet");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const fmt = (c) => new Intl.NumberFormat(locale, { style: "currency", currency }).format((c || 0) / 100);

  const referralLink = state.referral_code ? `${window.location.origin}/account/register?ref=${state.referral_code}` : "";
  const copyCode = async () => {
    if (!state.referral_code) return;
    try {
      await navigator.clipboard.writeText(state.referral_code);
      setCopied(true);
      toast.success("Code copié !");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Impossible de copier"); }
  };
  const copyLink = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success("Lien de parrainage copié !");
    } catch { toast.error("Impossible de copier"); }
  };

  const loyaltyEnabled = settings?.loyalty_enabled !== false;
  const cashbackPct = settings?.loyalty_cashback_pct || 0;
  const referralBonus = settings?.loyalty_referral_bonus_cents || 0;

  if (state.loading) {
    return <div className="py-12 text-center text-[hsl(var(--muted-foreground))]"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  }

  const REASON_LABELS = {
    order_cashback: "Cashback commande",
    order_redeem: "Utilisation en commande",
    referral_join: "Bonus parrainage (inscription)",
    referral_sponsor: "Bonus parrainage (filleul)",
    admin_adjust: "Ajustement agence",
    manual: "Ajustement manuel",
    refund: "Remboursement",
  };

  return (
    <div className="space-y-6" data-testid="account-wallet">
      {/* Balance card */}
      <Card className="p-6 md:p-8 bg-gradient-to-br from-[color:var(--brand-navy)] via-[color:var(--brand-navy)] to-[color:var(--brand-sky)] text-white overflow-hidden relative">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute right-6 top-6 opacity-80">
          <Award className="w-10 h-10" />
        </div>
        <div className="text-xs uppercase tracking-[0.2em] opacity-80 font-semibold">Solde de votre wallet</div>
        <div className="font-display text-4xl md:text-5xl font-black mt-2" data-testid="wallet-balance">{fmt(state.balance_cents)}</div>
        {loyaltyEnabled && cashbackPct > 0 && (
          <div className="mt-3 text-sm opacity-90 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4" />
            {cashbackPct}% de cashback crédité sur chaque commande payée
          </div>
        )}
      </Card>

      {/* Referral card */}
      {state.referral_code && (
        <Card className="p-6 border-[color:var(--brand-sky)]/20" data-testid="referral-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[color:var(--brand-sky)]/10 flex items-center justify-center shrink-0">
              <Gift className="w-5 h-5 text-[color:var(--brand-sky)]" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-xl font-bold text-[color:var(--brand-navy)]">Parrainez vos proches</h2>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                {referralBonus > 0
                  ? `Vous et votre filleul recevez ${fmt(referralBonus)} crédités sur vos wallets à son inscription.`
                  : "Partagez votre code parrain avec vos proches."}
              </p>

              <div className="mt-4 grid gap-3">
                <div>
                  <Label className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Votre code</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="px-4 py-3 rounded-lg border-2 border-dashed border-[color:var(--brand-sky)]/40 bg-[color:var(--brand-sky)]/5 font-mono text-lg font-bold tracking-widest text-[color:var(--brand-navy)] flex-1 text-center" data-testid="referral-code">{state.referral_code}</div>
                    <Button type="button" variant="outline" onClick={copyCode} data-testid="copy-referral-code">
                      {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                {referralLink && (
                  <div>
                    <Label className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Lien de parrainage</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input value={referralLink} readOnly className="text-xs font-mono" data-testid="referral-link" />
                      <Button type="button" variant="outline" onClick={copyLink} data-testid="copy-referral-link">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Transactions */}
      <Card className="p-6" data-testid="wallet-transactions">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold text-[color:var(--brand-navy)]">Historique</h2>
          <span className="text-xs text-[hsl(var(--muted-foreground))]">{state.transactions.length} opération{state.transactions.length > 1 ? "s" : ""}</span>
        </div>
        {state.transactions.length === 0 ? (
          <div className="py-10 text-center">
            <Sparkles className="w-10 h-10 text-[color:var(--brand-sky)]/30 mx-auto mb-3" />
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Aucune opération pour le moment.</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Passez commande pour commencer à cumuler du cashback.</p>
          </div>
        ) : (
          <div className="divide-y divide-[hsl(var(--border))]">
            {state.transactions.map((t) => {
              const amount = Number(t.amount_cents || 0);
              const isCredit = amount >= 0;
              return (
                <div key={t.id} className="py-3 flex items-center justify-between gap-4" data-testid={`wallet-tx-${t.id}`}>
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isCredit ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>
                      {isCredit ? <TrendingUp className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-[color:var(--brand-navy)] truncate">{REASON_LABELS[t.reason] || t.reason || "Opération"}</div>
                      {t.description && <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{t.description}</div>}
                      <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{t.created_at ? new Date(t.created_at).toLocaleString(locale) : ""}</div>
                    </div>
                  </div>
                  <div className={`font-bold tabular-nums ${isCredit ? "text-emerald-600" : "text-red-600"}`}>
                    {isCredit ? "+" : ""}{fmt(amount)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
